import React, { useState, useEffect } from 'react';
import { getRadioConnections } from '../services/firebase';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './ConnectionHistory.css';
import DataTable from '../components/common/DataTable';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ConnectionHistory = ({ radioName }) => {
  const [connections, setConnections] = useState([]);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 7)));
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'startTime', direction: 'desc' });
  const [stats, setStats] = useState({
    totalConnections: 0,
    uniqueIPs: 0,
    uniqueClients: 0,
    totalDuration: 0
  });

  const fetchConnections = async () => {
    try {
      setLoading(true);
      const connectionsData = await getRadioConnections(radioName, {
        startDate: startDate.getTime(),
        endDate: endDate.getTime()
      });

      // Procesar estadísticas
      const uniqueIPs = new Set();
      const uniqueClients = new Set();
      let totalDuration = 0;

      connectionsData.forEach(conn => {
        if (conn.ip) uniqueIPs.add(conn.ip);
        if (conn.clientName) uniqueClients.add(conn.clientName);
        if (conn.startTime && conn.endTime) {
          const duration = conn.endTime - conn.startTime;
          if (duration > 0) totalDuration += duration;
        }
      });

      setStats({
        totalConnections: connectionsData.length,
        uniqueIPs: uniqueIPs.size,
        uniqueClients: uniqueClients.size,
        totalDuration
      });

      setConnections(connectionsData);
      setLoading(false);
    } catch (error) {
      console.error('Error obteniendo conexiones:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnections();
    
  
  }, [radioName, startDate, endDate]);

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const columns = [
    { 
      key: 'clientName', 
      label: 'Cliente', 
      sortable: true 
    },
    { 
      key: 'ip', 
      label: 'IP', 
      sortable: true 
    },
    { 
      key: 'startTime', 
      label: 'Inicio', 
      sortable: true,
      render: (item) => formatDateTime(item.startTime)
    },
    { 
      key: 'endTime', 
      label: 'Fin', 
      sortable: true,
      render: (item) => formatDateTime(item.endTime)
    },
    { 
      key: 'duration', 
      label: 'Duración', 
      sortable: true,
      render: (item) => formatDuration(item.endTime - item.startTime)
    },
    { 
      key: 'status', 
      label: 'Estado', 
      sortable: true,
      render: (item) => (
        <span className={`status-badge ${item.status === 'active' ? 'active' : 'inactive'}`}>
          {item.status === 'active' ? 'Activo' : 'Finalizado'}
        </span>
      )
    },
    { 
      key: 'location', 
      label: 'Ubicación',
      render: (item) => {
        const geo = item.geodata || {};
        const loc = item.location || {};
        return (
          <div className="location-info">
            <div className="location-text">{geo.city || loc.city || 'N/A'}</div>
            <div className="location-text">
              {geo.region || loc.region}{geo.region && geo.country ? ', ' : ''}{geo.country || loc.country}
            </div>
          </div>
        );
      }
    },
    { 
      key: 'browser', 
      label: 'Navegador',
      render: (item) => {
        const meta = item.metadata || {};
        return (
          <div className="browser-info">
            <div className="browser-text">{meta.browser || 'N/A'}</div>
            <div className="browser-text">{meta.platform || ''}</div>
          </div>
        );
      }
    }
  ];

  const customActions = (
    <div className="date-filters">
      <div className="date-picker-wrapper">
        <DatePicker
          selected={startDate}
          onChange={date => setStartDate(date)}
          selectsStart
          startDate={startDate}
          endDate={endDate}
          maxDate={endDate}
          className="form-control"
          dateFormat="dd/MM/yyyy"
        />
      </div>
      <div className="date-picker-wrapper">
        <DatePicker
          selected={endDate}
          onChange={date => setEndDate(date)}
          selectsEnd
          startDate={startDate}
          endDate={endDate}
          minDate={startDate}
          className="form-control"
          dateFormat="dd/MM/yyyy"
        />
      </div>
    </div>
  );

  const handleExport = async () => {
    try {
      const workbook = XLSX.utils.book_new();
      
      // 1. Hoja de Conexiones Detalladas
      const exportData = connections.map(conn => ({
        'Cliente': conn.clientName,
        'IP': conn.ip,
        'Inicio': formatDateTime(conn.startTime),
        'Fin': formatDateTime(conn.endTime),
        'Duración': formatDuration(conn.endTime - conn.startTime),
        'Estado': conn.status === 'active' ? 'Activo' : 'Finalizado',
        'Ciudad': conn.geodata?.city || conn.location?.city || 'N/A',
        'Estado/Región': conn.geodata?.region || conn.location?.region || 'N/A',
        'País': conn.geodata?.country || conn.location?.country || 'N/A',
        'Latitud': conn.geodata?.latitude || conn.location?.latitude || 'N/A',
        'Longitud': conn.geodata?.longitude || conn.location?.longitude || 'N/A',
        'Navegador': conn.metadata?.browser || 'N/A',
        'Plataforma': conn.metadata?.platform || 'N/A',
        'Idioma': conn.metadata?.language || 'N/A',
        'Zona Horaria': conn.metadata?.timeZone || 'N/A',
        'Nombre de la Radio': radioName
      }));

      // 2. Análisis General
      const uniqueIPs = [...new Set(connections.map(conn => conn.ip))];
      const uniqueClients = [...new Set(connections.map(conn => conn.clientName))];
      
      // Agrupar conexiones por día
      const connectionsByDay = connections.reduce((acc, conn) => {
        const date = new Date(conn.startTime).toLocaleDateString();
        if (!acc[date]) acc[date] = [];
        acc[date].push(conn);
        return acc;
      }, {});

      // Calcular estadísticas generales
      const generalStats = [{
        'Métrica': 'Total de Conexiones',
        'Valor': connections.length
      }, {
        'Métrica': 'IPs Únicas',
        'Valor': uniqueIPs.length
      }, {
        'Métrica': 'Clientes Únicos',
        'Valor': uniqueClients.length
      }, {
        'Métrica': 'Promedio de Conexiones por Día',
        'Valor': (connections.length / Object.keys(connectionsByDay).length).toFixed(2)
      }, {
        'Métrica': 'Duración Total de Conexiones',
        'Valor': formatDuration(connections.reduce((acc, conn) => acc + (conn.endTime - conn.startTime), 0))
      }];

      // 3. Análisis por IP con validación de geolocalización
      const ipAnalysis = uniqueIPs.map(ip => {
        const ipConnections = connections.filter(conn => conn.ip === ip);
        
        // Agrupar conexiones por ubicación aproximada
        const locationGroups = ipConnections.reduce((groups, conn) => {
          const geoKey = conn.geodata ? 
            `${conn.geodata.latitude?.toFixed(2)}_${conn.geodata.longitude?.toFixed(2)}` : 
            'unknown';
          
          if (!groups[geoKey]) {
            groups[geoKey] = {
              connections: [],
              location: conn.geodata ? {
                city: conn.geodata.city,
                region: conn.geodata.region,
                country: conn.geodata.country
              } : null
            };
          }
          groups[geoKey].connections.push(conn);
          return groups;
        }, {});

        // Analizar cada grupo de ubicación
        const locationAnalysis = Object.entries(locationGroups).map(([geoKey, group]) => {
          const totalDuration = group.connections.reduce((acc, conn) => 
            acc + (conn.endTime - conn.startTime), 0);
          const avgDuration = totalDuration / group.connections.length;

          return {
            'IP': ip,
            'Ubicación': group.location ? 
              `${group.location.city}, ${group.location.region}, ${group.location.country}` : 
              'Ubicación Desconocida',
            'Total Conexiones': group.connections.length,
            'Duración Total': formatDuration(totalDuration),
            'Duración Promedio': formatDuration(avgDuration),
            'Primera Conexión': formatDateTime(Math.min(...group.connections.map(conn => conn.startTime))),
            'Última Conexión': formatDateTime(Math.max(...group.connections.map(conn => conn.endTime))),
            'Clientes Asociados': [...new Set(group.connections.map(conn => conn.clientName))].join(', '),
            'Posible Sobrelapamiento': group.connections.some((conn1, i) => 
              group.connections.some((conn2, j) => 
                i !== j && 
                conn1.startTime <= conn2.endTime && 
                conn2.startTime <= conn1.endTime
              )) ? 'Sí' : 'No'
          };
        });

        return locationAnalysis;
      }).flat();

      // 4. Análisis por Cliente con manejo uniforme
      const clientAnalysis = uniqueClients.map(client => {
        const clientConnections = connections.filter(conn => conn.clientName === client);
        
        // Para todos los clientes, agrupar por IP y ubicación
        const userGroups = clientConnections.reduce((groups, conn) => {
          const geoKey = conn.geodata ? 
            `${conn.ip}_${conn.geodata.latitude?.toFixed(2)}_${conn.geodata.longitude?.toFixed(2)}` : 
            conn.ip;
          
          if (!groups[geoKey]) {
            groups[geoKey] = {
              connections: [],
              ip: conn.ip,
              location: conn.geodata ? {
                city: conn.geodata.city,
                region: conn.geodata.region,
                country: conn.geodata.country
              } : null
            };
          }
          groups[geoKey].connections.push(conn);
          return groups;
        }, {});

        return Object.entries(userGroups).map(([geoKey, group]) => {
          const totalDuration = group.connections.reduce((acc, conn) => 
            acc + (conn.endTime - conn.startTime), 0);
          const avgDuration = totalDuration / group.connections.length;

          // Detectar conexiones simultáneas
          const simultaneousConnections = group.connections.some((conn1, i) => 
            group.connections.some((conn2, j) => 
              i !== j && 
              conn1.startTime <= conn2.endTime && 
              conn2.startTime <= conn1.endTime
            ));

          // Calcular horas de conexión por día
          const connectionsByDay = group.connections.reduce((acc, conn) => {
            const date = new Date(conn.startTime).toLocaleDateString();
            if (!acc[date]) acc[date] = 0;
            acc[date] += (conn.endTime - conn.startTime) / (1000 * 60 * 60); // Convertir a horas
            return acc;
          }, {});

          const avgHoursPerDay = Object.values(connectionsByDay).reduce((sum, hours) => sum + hours, 0) / 
            Object.keys(connectionsByDay).length;

          return {
            'Cliente': client === 'Usuario Externo' ? 
              'Usuario Externo' : client,
            'Ubicación': group.location ? 
              `${group.location.city}, ${group.location.region}, ${group.location.country}` : 
              'Ubicación Desconocida',
            'IP': group.ip,
            'Total Conexiones': group.connections.length,
            'Duración Total': formatDuration(totalDuration),
            'Duración Promedio': formatDuration(avgDuration),
            'Primera Conexión': formatDateTime(Math.min(...group.connections.map(conn => conn.startTime))),
            'Última Conexión': formatDateTime(Math.max(...group.connections.map(conn => conn.endTime))),
            'Días con Actividad': Object.keys(connectionsByDay).length,
            'Promedio Horas por Día': avgHoursPerDay.toFixed(2),
            'Conexiones Simultáneas': simultaneousConnections ? 'Sí' : 'No',
            'Días Conectado': Object.entries(connectionsByDay)
              .map(([date, hours]) => `${date}: ${hours.toFixed(2)}h`)
              .join('; '),
            'Navegador': group.connections[0].metadata?.browser || 'N/A',
            'Plataforma': group.connections[0].metadata?.platform || 'N/A'
          };
        });
      }).flat();

      // 5. Análisis por Día
      const dailyAnalysis = Object.entries(connectionsByDay).map(([date, conns]) => {
        const totalDuration = conns.reduce((acc, conn) => acc + (conn.endTime - conn.startTime), 0);
        return {
          'Fecha': date,
          'Total Conexiones': conns.length,
          'Clientes Únicos': new Set(conns.map(conn => conn.clientName)).size,
          'IPs Únicas': new Set(conns.map(conn => conn.ip)).size,
          'Duración Total': formatDuration(totalDuration),
          'Duración Promedio por Conexión': formatDuration(totalDuration / conns.length)
        };
      });

      // 6. Análisis por Región
      const regionAnalysis = connections.reduce((acc, conn) => {
        const region = {
          city: conn.geodata?.city || 'Desconocido',
          state: conn.geodata?.region || 'Desconocido',
          country: conn.geodata?.country || 'Desconocido'
        };
        
        const regionKey = `${region.state}, ${region.country}`;
        const cityKey = `${region.city}, ${region.state}, ${region.country}`;

        // Análisis por Estado/Región
        if (!acc.regions[regionKey]) {
          acc.regions[regionKey] = {
            conexiones: 0,
            duracionTotal: 0,
            clientesUnicos: new Set(),
            ipsUnicas: new Set(),
            ciudades: new Set(),
            diasActivos: new Set()
          };
        }
        acc.regions[regionKey].conexiones++;
        acc.regions[regionKey].duracionTotal += (conn.endTime - conn.startTime);
        acc.regions[regionKey].clientesUnicos.add(conn.clientName);
        acc.regions[regionKey].ipsUnicas.add(conn.ip);
        acc.regions[regionKey].ciudades.add(region.city);
        acc.regions[regionKey].diasActivos.add(new Date(conn.startTime).toLocaleDateString());

        // Análisis por Ciudad
        if (!acc.cities[cityKey]) {
          acc.cities[cityKey] = {
            conexiones: 0,
            duracionTotal: 0,
            clientesUnicos: new Set(),
            ipsUnicas: new Set(),
            diasActivos: new Set()
          };
        }
        acc.cities[cityKey].conexiones++;
        acc.cities[cityKey].duracionTotal += (conn.endTime - conn.startTime);
        acc.cities[cityKey].clientesUnicos.add(conn.clientName);
        acc.cities[cityKey].ipsUnicas.add(conn.ip);
        acc.cities[cityKey].diasActivos.add(new Date(conn.startTime).toLocaleDateString());

        return acc;
      }, { regions: {}, cities: {} });

      // Convertir el análisis por región a formato para Excel
      const regionStats = Object.entries(regionAnalysis.regions).map(([region, stats]) => {
        const [state, country] = region.split(', ');
        return {
          'Estado': state || 'Desconocido',
          'País': country || 'Desconocido',
          'Total Conexiones': stats.conexiones,
          'Clientes Únicos': stats.clientesUnicos.size,
          'IPs Únicas': stats.ipsUnicas.size,
          'Ciudades': stats.ciudades.size,
          'Días con Actividad': stats.diasActivos.size,
          'Duración Total': formatDuration(stats.duracionTotal),
          'Promedio Duración por Conexión': formatDuration(stats.duracionTotal / stats.conexiones),
          'Promedio Conexiones por Día': (stats.conexiones / stats.diasActivos.size).toFixed(2),
          'Ciudades en la Región': Array.from(stats.ciudades).join(', ')
        };
      });

      // Convertir el análisis por ciudad a formato para Excel
      const cityStats = Object.entries(regionAnalysis.cities).map(([city, stats]) => {
        const [cityName, state, country] = city.split(', ');
        return {
          'Ciudad': cityName || 'Desconocido',
          'Estado': state || 'Desconocido',
          'País': country || 'Desconocido',
          'Total Conexiones': stats.conexiones,
          'Clientes Únicos': stats.clientesUnicos.size,
          'IPs Únicas': stats.ipsUnicas.size,
          'Días con Actividad': stats.diasActivos.size,
          'Duración Total': formatDuration(stats.duracionTotal),
          'Promedio Duración por Conexión': formatDuration(stats.duracionTotal / stats.conexiones),
          'Promedio Conexiones por Día': (stats.conexiones / stats.diasActivos.size).toFixed(2),
          'Clientes': Array.from(stats.clientesUnicos).join(', ')
        };
      });

      // Agregar todas las hojas al libro en el nuevo orden
      const safeSheetName = (name) => {
        // Reemplazar caracteres no permitidos con guiones
        return name.replace(/[:\\\/\?\*\[\]]/g, '-');
      };

      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(exportData), safeSheetName('Conexiones'));
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(ipAnalysis), safeSheetName('Analisis IP'));
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(clientAnalysis), safeSheetName('Analisis Cliente'));
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(dailyAnalysis), safeSheetName('Analisis Dia'));
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(regionStats), safeSheetName('Analisis Region'));
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(cityStats), safeSheetName('Analisis Ciudad'));
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(generalStats), safeSheetName('Estadisticas'));

      // Agregar estilos y formato a las hojas
      const sheets = ['Conexiones', 'Analisis IP', 'Analisis Cliente', 
                     'Analisis Dia', 'Analisis Region', 'Analisis Ciudad', 
                     'Estadisticas'];

      sheets.forEach(sheetName => {
        const worksheet = workbook.Sheets[sheetName];
        if (worksheet['!ref']) {
          const range = XLSX.utils.decode_range(worksheet['!ref']);
          // Ajustar el ancho de las columnas
          const wscols = [];
          for (let i = range.s.c; i <= range.e.c; i++) {
            wscols.push({ wch: 20 }); // width in characters
          }
          worksheet['!cols'] = wscols;
        }
      });

      XLSX.writeFile(workbook, `conexiones_${radioName}_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      toast.success('Reporte exportado exitosamente');
    } catch (error) {
      console.error('Error exportando datos:', error);
      toast.error('Error al exportar el reporte');
    }
  };

  return (
    <div className="connection-history">
      <DataTable
        title="Historial de Conexiones"
        data={connections}
        columns={columns}
        loading={loading}
        sortConfig={sortConfig}
        onSort={handleSort}
        customActions={customActions}
        exportable={true}
        onExport={handleExport}
        searchable={true}
        pagination={true}
        rowsPerPageOptions={[10, 25, 50, 100]}
        showFooter={true}
        footerProps={{
          showFirstLastButtons: true,
          labelRowsPerPage: "Filas por página:",
          labelDisplayedRows: ({ from, to, count }) => 
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`,
          nextIconButtonText: "Siguiente página",
          backIconButtonText: "Página anterior",
          firstPageButtonText: "Primera página",
          lastPageButtonText: "Última página"
        }}
      />
    </div>
  );
};

export default ConnectionHistory; 